import type { AnyAction } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import createGenericSlice from '@/store/reducers/sliceTemplate';
import { handleResponseError } from '@/core/utils/handleResponseError';
import type {
	MeCardData,
	MeCardWire,
	MeCardsState,
} from '@/store/types/meCardsTypes';
import { normalizeMeCard } from '@/store/types/meCardsTypes';

// Раздел «Я» в профиле — карточки-характеристики (тип личности, темперамент,
// IQ, …). Слайс хранит UI-ready форму (`MeCardData`), нормализация
// snake→camel + enum-маппинг статуса живёт в `meCardsTypes.ts`.

const meCardsBaseSlice = createGenericSlice<MeCardData>('meCards');

const initialState: MeCardsState = {
	...meCardsBaseSlice.getInitialState(),
};

const combinedSlice = createSlice({
	name: 'meCards',
	initialState,
	reducers: {
		...meCardsBaseSlice.caseReducers,

		/**
		 * Положить в стор UI-shape (`MeCardData[]`) из wire-shape (`MeCardWire[]`).
		 * Полная подмена — endpoint отдаёт полный набор карточек, пагинации нет.
		 */
		setMeCardsNormalized(state, action: PayloadAction<MeCardWire[]>) {
			state.data = action.payload.map(normalizeMeCard);
			state.isFetching = false;
			state.hasFetchError = false;
		},
	},
});

export const {
	fetchDataStart,
	fetchDataError,
	setDataLoaded,
	resetError,
	setMeCardsNormalized,
} = combinedSlice.actions;

/** Загрузка карточек раздела «Я» с бэкенда. */
export const fetchMeCardsData = () => async (dispatch: Dispatch<AnyAction>) => {
	dispatch(combinedSlice.actions.fetchDataStart());
	try {
		const { data } = await apiUrl.get<{ data: MeCardWire[] }>(
			API_ENDPOINTS.profileMeCards,
		);
		dispatch(combinedSlice.actions.setMeCardsNormalized(data.data));
	} catch (error) {
		handleResponseError(error, (errorMessage, isNetworkError) => {
			dispatch(
				combinedSlice.actions.fetchDataError({ errorMessage, isNetworkError }),
			);
		});
	}
};

export default combinedSlice.reducer;
export { combinedSlice as meCardsSlice };
